/**
 * Inspection rounds — React Query hooks.
 *
 * Kept apart from queries.ts so the rounds can be read without the rest of
 * the inspection screens. Keys are namespaced by site, as everywhere else, and
 * start with "inspection-rounds" so a write can refresh every reading of them
 * with one prefix.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import { DataSourceError } from "./dataSource";

export const roundKeys = {
  all: (siteId: string) => ["inspection-rounds", siteId] as const,
  rounds: (siteId: string, templates: readonly string[]) =>
    ["inspection-rounds", siteId, [...templates].sort().join(",")] as const,
};

/** Completed rounds of these checklists, with every answer and the work each raised. */
export function useInspectionRounds(templates: readonly string[], opts: { enabled?: boolean; limit?: number } = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: roundKeys.rounds(site.id, templates),
    queryFn: () =>
      source.listInspectionRounds({ siteId: site.id, templates, ...(opts.limit ? { limit: opts.limit } : {}) }),
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    /* a refusal is an answer: asking again changes nothing until the role does */
    retry: (n, e) => !(e instanceof DataSourceError && e.status === 403) && n < 2,
  });
}
